import type * as React from "react";
import { Box } from "../../components/ui/box";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

/**
 * The selector's empty state. There are TWO of them and they are not
 * interchangeable:
 *
 * - `no-results` — the pool has items and the query matched none. The way out
 *   is to relax the query, so the copy names the search.
 * - `no-items` — the pool is empty before anything is typed. There is no query
 *   to relax; the way out is to go create the thing. Telling someone "no
 *   results found" when they have not searched sends them looking for a filter
 *   that isn't there.
 *
 * A pool that is still fetching is neither: `pool.loading` suppresses both,
 * because an unfetched pool and an empty one have the same item count.
 */

export type EntitySelectorEmptyReason = "no-items" | "no-results";

/** What the empty state is standing in for, for copy that quotes the query. */
export type EntitySelectorEmptyState = {
  reason: EntitySelectorEmptyReason;
  /** The current query. Empty string when `reason` is `no-items`. */
  search: string;
};

/** One state's rendered body. Every part is optional — a bare message is the
 *  common case, and the icon/description/action slots are for a pool whose
 *  emptiness the consumer can actually help resolve. */
export type EntitySelectorEmptyBody = Omittable<{
  message?: React.ReactNode;
  /** Second line, quieter — where the missing items come from. */
  description?: React.ReactNode;
  /** Glyph above the text. */
  icon?: IconName;
  /** Affordance below the text (e.g. a button that opens a create flow). */
  action?: React.ReactNode;
}>;

export type EntitySelectorEmptyProps = Omittable<{
  /**
   * Message for whichever state is showing. A function receives the resolved
   * state, for copy that quotes the query. Per-reason props below win over it.
   */
  children?:
    | React.ReactNode
    | ((state: EntitySelectorEmptyState) => React.ReactNode);
  /** Body when a query matched nothing. */
  noResults?: EntitySelectorEmptyBody;
  /** Body when the pool has no items at all. */
  noItems?: EntitySelectorEmptyBody;
}>;

function EntitySelectorEmpty({
  children,
  noResults,
  noItems,
}: EntitySelectorEmptyProps) {
  const { search, activePool, getPool, getFilteredItems, getPinnedItems, config } =
    useEntitySelector();

  const pool = getPool(activePool);
  const query = search.trim();
  const reason: EntitySelectorEmptyReason = query ? "no-results" : "no-items";

  // A virtual pool sized by `total` has rows it has not loaded yet, so an empty
  // loaded prefix is a pending fetch, not an empty pool. Searching bypasses
  // virtual mode (the pool renders dense filtered items), so the guard is
  // limited to the unsearched case.
  const hasUnloadedRows =
    !query && pool?.total != null && pool.total > 0;

  const isEmpty =
    pool != null &&
    !pool.loading &&
    !hasUnloadedRows &&
    getFilteredItems(activePool).length === 0 &&
    getPinnedItems(activePool).length === 0;

  if (!isEmpty) {
    return null;
  }

  const body = (reason === "no-results" ? noResults : noItems) ?? {};

  const fallbackMessage =
    reason === "no-results"
      ? (config.emptyMessage ?? "No results found")
      : (config.noItemsMessage ?? config.emptyMessage ?? "Nothing to select");

  const childMessage =
    typeof children === "function" ? children({ reason, search }) : children;

  const message = body.message ?? childMessage ?? fallbackMessage;

  return (
    <Box
      direction="column"
      align={body.icon || body.description || body.action ? "center" : "start"}
      gap="xs"
      paddingX="sm"
      paddingY={body.icon || body.description || body.action ? "md" : "xs"}
      data-slot="entity-selector-empty"
    >
      {body.icon ? <Icon name={body.icon} size="lg" color="muted" /> : null}
      <Text size="xs" variant="muted">
        {message}
      </Text>
      {body.description ? (
        <Text size="xs" variant="muted">
          {body.description}
        </Text>
      ) : null}
      {body.action}
    </Box>
  );
}

EntitySelectorEmpty.displayName = "EntitySelectorEmpty";

export { EntitySelectorEmpty };
